import { OryxGameClient } from "@westpierstudio/core-module-oryx/game";
import { RequestMessage } from "@westpierstudio/core-module-oryx/services/comms/types/BaseTypes";
import { PeriodicInspectionEvent } from "@westpierstudio/core-module-oryx/services/comms/types/PeriodicInspection";
import { GameEventRequestMessage } from "@westpierstudio/core-module-oryx/services/comms/types/Requests";
import { AuthenticateResponseMessage } from "@westpierstudio/core-module-oryx/services/comms/types/Responses";
import { GameEvent, GameRequest } from "servertypes/ServerEvents";
import { OpenGameEvent } from "servertypes/gameevents/OpenGame";
import { SpinEvent } from "servertypes/gameevents/Spin";
import { VSSpinEvent } from "servertypes/gameevents/VSSpin";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { SpinErrorEvent } from "servertypes/gameevents/SpinError";
import { BetData, VSOption } from "servertypes/SharedTypes";
import { Signal } from "type-signals";
import { GenericGameRequestMessage, VSSpinRequest } from "servertypes/requests/GenericGameRequest";
import { SmallSpinEvent } from "servertypes/gameevents/SmallSpin";

class DragonsVsPandasGameClientSignals {
    openGame = new Signal<(event: OpenGameEvent)=>void>();
    smallSpin = new Signal<(event: SmallSpinEvent)=>void>();
    spin = new Signal<(event: SpinEvent)=>void>();
    vsSpin = new Signal<(event: VSSpinEvent)=>void>();
    freeSpin = new Signal<(event: FreeSpinEvent)=>void>();
    spinError = new Signal<(event: SpinErrorEvent)=>void>();
    periodicInspection = new Signal<(event: PeriodicInspectionEvent)=>void>();
    activateFreeRoundError = new Signal<(code: number)=>void>();
}

export class DragonsVsPandasGameClient extends OryxGameClient {
    signals = new DragonsVsPandasGameClientSignals();

    handleIncomingGameEvent(gameEvent: GameEventRequestMessage, _triggeringRequest: RequestMessage): void {

        switch (gameEvent.body.event as GameEvent) {
            case GameEvent.OPEN_GAME:
                this.signals.openGame.dispatch(gameEvent);
                break;
            case GameEvent.SMALL_SPIN:
                this.signals.smallSpin.dispatch(gameEvent);
                break;
            case GameEvent.SPIN:
                this.signals.spin.dispatch(gameEvent);
                break;
            case GameEvent.VS_SPIN:
                this.signals.vsSpin.dispatch(gameEvent);
                break;
            case GameEvent.FREE_SPIN:
                this.signals.freeSpin.dispatch(gameEvent);
                break;
            case GameEvent.SPIN_ERROR:
                this.signals.spinError.dispatch(gameEvent);
                break;
            default:
                this.missingHandlerWarning(gameEvent.header.name);
        }
    }

    handlePeriodicInspectionEvent(periodicInspection: PeriodicInspectionEvent): void {
        this.signals.periodicInspection.dispatch(periodicInspection);
    }

    override handleAuthenticate(response: AuthenticateResponseMessage, triggeringRequest: RequestMessage) {
        super.handleAuthenticate(response, triggeringRequest);
        this.sendOpenGameRequest();
    }

    sendSmallSpinRequest(betData: BetData, freeRoundId?: string): void {

        const {coinsPerBet, coinSize, playerBet} = betData;

        const messageBody: GenericGameRequestMessage["body"] = {
            type: GameRequest.SMALL_SPIN,
            data: {
                coinsPerBet,
                coinSize,
                playerBet
            }
        };

        this.addFreeRoundIdIfDefined(messageBody, freeRoundId);
        this.services.comms.sendGameActionRequest(messageBody);
    }

    sendSpinRequest(betData: BetData, freeRoundId?: string): void {

        const {coinsPerBet, coinSize, playerBet} = betData;

        const messageBody: GenericGameRequestMessage["body"] = {
            type: GameRequest.SPIN,
            data: {
                coinsPerBet,
                coinSize,
                playerBet
            }
        };

        this.addFreeRoundIdIfDefined(messageBody, freeRoundId);
        this.services.comms.sendGameActionRequest(messageBody);
    }

    sendVSSpinRequest(betData: BetData, pickIndex: VSOption, freeRoundId?: string): void {

        if (pickIndex === undefined) {
            throw new Error(`No pickIndex set for outbound VSSpinRequest`);
        }

        const {coinsPerBet, coinSize, playerBet} = betData;

        const messageBody: VSSpinRequest["body"] = {
            type: GameRequest.VS_SPIN,
            data: {
                coinsPerBet,
                coinSize,
                playerBet,
                pickIndex
            }
        };

        this.addFreeRoundIdIfDefined(messageBody, freeRoundId);
        this.services.comms.sendGameActionRequest(messageBody);
    }

    sendFreeSpinRequest(betData: BetData, freeRoundId?: string): void {

        const {coinsPerBet, coinSize, playerBet} = betData;

        const messageBody: GenericGameRequestMessage["body"] = {
            type: GameRequest.FREE_SPIN,
            data: {
                coinsPerBet,
                coinSize,
                playerBet
            }
        };

        this.addFreeRoundIdIfDefined(messageBody, freeRoundId);
        this.services.comms.sendGameActionRequest(messageBody);
    }

    private addFreeRoundIdIfDefined(messageBody: {freeRoundId?: string}, freeRoundId?: string): void {

        if (freeRoundId !== undefined) {
            messageBody.freeRoundId = freeRoundId;
        }
    }

    private missingHandlerWarning(event: string): void {
        console.warn(`No handler specified for: `, event);
    }
}